/**
 * Centralized Error Notification Service
 * Provides unified error handling, formatting, and user notifications
 * 
 * This service bridges ErrorHandler (formatting) and NotificationContext (display)
 * with defensive checks to handle initialization edge cases.
 */

import ErrorHandler from '../utils/errorHandler';

class ErrorNotificationService {
  constructor() {
    this.notificationContext = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the service with NotificationContext methods
   * @param {Object} contextMethods - Methods from NotificationContext
   * @param {Function} contextMethods.show - Show notification
   * @param {Function} contextMethods.success - Show success notification
   * @param {Function} contextMethods.error - Show error notification
   * @param {Function} contextMethods.warning - Show warning notification
   * @param {Function} contextMethods.info - Show info notification
   * @param {Function} contextMethods.paymentSuccess - Show payment success notification
   */
  initialize(contextMethods) {
    if (!contextMethods) {
      console.warn('ErrorNotificationService: No context methods provided');
      return;
    }

    this.notificationContext = {
      show: contextMethods.show,
      success: contextMethods.success,
      error: contextMethods.error,
      warning: contextMethods.warning,
      info: contextMethods.info,
      paymentSuccess: contextMethods.paymentSuccess
    };

    this.isInitialized = true;
  }

  /**
   * Check if service is initialized (defensive check)
   * @returns {boolean}
   */
  _checkInitialized() {
    if (!this.isInitialized || !this.notificationContext) {
      console.warn('ErrorNotificationService not initialized, logging only');
      return false;
    }
    return true;
  }

  /**
   * Determine error category and type
   * @param {Error} error - The error object
   * @returns {Object} Error category and type
   */
  _categorizeError(error) {
    if (!error) {
      return { category: 'unknown', type: 'error' };
    }

    // Subscription errors
    if (error.code === 'SUBSCRIPTION_EXPIRED' || 
        error.code === 'OPERATION_LIMIT_EXCEEDED' ||
        error.code === 'STORAGE_LIMIT_EXCEEDED' ||
        error.code === 'FEATURE_LIMIT_EXCEEDED' ||
        error.subscriptionExpired) {
      return { category: 'subscription', type: 'error' };
    }

    // Validation errors
    if (error.code === 'VALIDATION_ERROR' ||
        error.code === 'INVALID_INPUT' ||
        error.validationError ||
        error.fieldName) {
      return { category: 'validation', type: 'error' };
    }

    // Firebase Auth errors
    if (error.code && error.code.startsWith('auth/')) {
      return { category: 'firebase', subcategory: 'auth', type: 'error' };
    }

    // Firebase Firestore errors - but check for network-related codes first
    // unavailable and failed-precondition often indicate offline/network issues
    if (error.code && (error.code === 'unavailable' || error.code === 'failed-precondition')) {
      return { category: 'network', type: 'error' };
    }
    
    // Other Firebase Firestore errors
    if (error.code && (error.code.startsWith('firestore/') || 
        ['permission-denied', 'not-found', 'already-exists', 'resource-exhausted'].includes(error.code))) {
      return { category: 'firebase', subcategory: 'firestore', type: 'error' };
    }

    // Network errors (check error code first, then message)
    if (error.code && (
        error.code.includes('network') ||
        error.code === 'deadline-exceeded' ||
        error.code === 'unavailable' ||
        error.code === 'failed-precondition'
      )) {
      return { category: 'network', type: 'error' };
    }
    
    if (error.message && (
        error.message.includes('network') ||
        error.message.includes('Network') ||
        error.message.includes('timeout') ||
        error.message.includes('offline') ||
        error.message.includes('Offline') ||
        error.message.includes('unavailable') ||
        error.message.includes('fetch')
      )) {
      return { category: 'network', type: 'error' };
    }

    // Default
    return { category: 'unknown', type: 'error' };
  }

  /**
   * Format error message using ErrorHandler
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   * @returns {string} Formatted error message
   */
  _formatError(error, options = {}) {
    if (!error) {
      return options.defaultMessage || 'An unexpected error occurred';
    }

    // Use ErrorHandler's formatError method
    return ErrorHandler.formatError(error);
  }

  /**
   * Main error handler - routes to appropriate handler based on error type
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   * @param {string} options.context - Context where error occurred
   * @param {string} options.title - Custom error title
   * @param {string} options.severity - Error severity (error, warning, info)
   * @param {number} options.duration - Notification duration in ms
   * @param {boolean} options.showNotification - Whether to show notification (default: true)
   * @param {string} options.category - Custom category override
   */
  handleError(error, options = {}) {
    // Always log the error first
    const context = options.context || 'Unknown';
    ErrorHandler.logError(error, context);

    // Check if service is initialized
    if (!this._checkInitialized()) {
      // Still log, but don't show notification
      return;
    }

    // Determine error category
    const errorCategory = this._categorizeError(error);
    const category = options.category || errorCategory.category;

    // Route to appropriate handler
    switch (category) {
      case 'subscription':
        return this.handleSubscriptionError(error, options);
      case 'validation':
        return this.handleValidationError(error, options);
      case 'firebase':
        return this.handleFirebaseError(error, options);
      case 'network':
        return this.handleNetworkError(error, options);
      default:
        return this._showNotification(error, options, 'error');
    }
  }

  /**
   * Handle subscription-related errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  handleSubscriptionError(error, options = {}) {
    if (!this._checkInitialized()) {
      ErrorHandler.logError(error, options.context || 'Subscription');
      return;
    }

    const formattedMessage = this._formatError(error, options);
    const title = options.title || this._getSubscriptionErrorTitle(error);
    
    // Determine severity based on error code
    let severity = 'error';
    if (error.code === 'SUBSCRIPTION_EXPIRED') {
      severity = 'error';
    } else if (error.code === 'OPERATION_LIMIT_EXCEEDED' || 
               error.code === 'STORAGE_LIMIT_EXCEEDED') {
      severity = 'warning';
    }

    // Add days remaining info if available
    let message = formattedMessage;
    if (error.daysRemaining !== undefined) {
      if (error.daysRemaining <= 0) {
        message = `${formattedMessage}\n\nYour subscription expired ${Math.abs(error.daysRemaining)} day(s) ago.`;
      } else {
        message = `${formattedMessage}\n\n${error.daysRemaining} day(s) remaining.`;
      }
    }

    this._showNotification(
      { ...error, message },
      { ...options, title, severity: options.severity || severity },
      options.severity || severity
    );
  }

  /**
   * Get subscription error title based on error code
   * @param {Error} error - The error object
   * @returns {string} Error title
   */
  _getSubscriptionErrorTitle(error) {
    if (error.code === 'SUBSCRIPTION_EXPIRED') {
      return 'Subscription Expired';
    } else if (error.code === 'OPERATION_LIMIT_EXCEEDED') {
      return 'Operation Limit Reached';
    } else if (error.code === 'STORAGE_LIMIT_EXCEEDED') {
      return 'Storage Limit Reached';
    } else if (error.code === 'FEATURE_LIMIT_EXCEEDED') {
      return 'Feature Limit Reached';
    }
    return 'Subscription Error';
  }

  /**
   * Handle validation errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  handleValidationError(error, options = {}) {
    if (!this._checkInitialized()) {
      ErrorHandler.logError(error, options.context || 'Validation');
      return;
    }

    const formattedMessage = this._formatError(error, options);
    const title = options.title || this._getValidationErrorTitle(error, options);

    this._showNotification(
      error,
      { ...options, title, severity: options.severity || 'error' },
      options.severity || 'error'
    );
  }

  /**
   * Get validation error title
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   * @returns {string} Error title
   */
  _getValidationErrorTitle(error, options) {
    if (options.fieldName) {
      return `Invalid ${options.fieldName}`;
    }
    return 'Validation Error';
  }

  /**
   * Handle Firebase errors (Auth or Firestore)
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  handleFirebaseError(error, options = {}) {
    if (!this._checkInitialized()) {
      ErrorHandler.logError(error, options.context || 'Firebase');
      return;
    }

    const formattedMessage = this._formatError(error, options);
    const title = options.title || 'Firebase Error';

    this._showNotification(
      error,
      { ...options, title, severity: options.severity || 'error' },
      options.severity || 'error'
    );
  }

  /**
   * Handle network errors
   * @param {Error} error - The error object
   * @param {Object} options - Error handling options
   */
  handleNetworkError(error, options = {}) {
    if (!this._checkInitialized()) {
      ErrorHandler.logError(error, options.context || 'Network');
      return;
    }

    // Check if device is actually offline
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    
    // Enhance error message if offline
    if (isOffline && (!error.code || error.code === 'unavailable' || error.code === 'failed-precondition')) {
      error.code = error.code || 'OFFLINE';
      if (!error.message || error.message.includes('unavailable') || error.message.includes('failed-precondition')) {
        error.message = 'You are currently offline. Please check your internet connection and try again.';
      }
    }

    const formattedMessage = this._formatError(error, options);
    const title = options.title || (isOffline ? 'Offline' : 'Network Error');

    this._showNotification(
      error,
      { ...options, title, severity: options.severity || 'error' },
      options.severity || 'error'
    );
  }

  /**
   * Show notification using NotificationContext
   * @param {Error} error - The error object
   * @param {Object} options - Notification options
   * @param {string} severity - Notification severity
   */
  _showNotification(error, options = {}, severity = 'error') {
    // Check if notification should be shown
    if (options.showNotification === false) {
      return;
    }

    const message = this._formatError(error, options);
    const title = options.title || 'Error';
    const duration = options.duration !== undefined ? options.duration : 
                     (severity === 'error' ? 7000 : 5000);

    // Use appropriate notification method based on severity
    switch (severity) {
      case 'success':
        this.notificationContext.success(message, { title, duration });
        break;
      case 'warning':
        this.notificationContext.warning(message, { title, duration });
        break;
      case 'info':
        this.notificationContext.info(message, { title, duration });
        break;
      default:
        this.notificationContext.error(message, { title, duration });
    }
  }
}

// Export singleton instance
const errorNotificationService = new ErrorNotificationService();
export default errorNotificationService;


